// Shared between AutomationRuleDialog.tsx (flat form) and the flow-graph
// editor (AutomationFlowNodes.tsx) — kept in one place so the two views of
// the same rule never drift out of sync on what fields/operators/actions
// are offered.

export const TICKET_EVENTS = new Set([
  'ticket_created', 'ticket_status_changed', 'ticket_assigned',
  'ticket_priority_changed', 'sla_at_risk', 'sla_breached',
]);

export const OPERATORS = [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
];

export const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'assignee_id', label: 'Assignee' },
  { value: 'team_id', label: 'Team' },
  { value: 'inbox_id', label: 'Inbox' },
  { value: 'message_content', label: 'Message Content' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_phone', label: 'Contact Phone' },
];

// 2026-08-08: fields available in a ticket-domain event's context (see
// ticketing_service.py._publish_ticket_event's payload) — "status" and
// "priority" are shared with the conversation field list above (same key
// names, different domain), category_id/ticket_id/assigned_agent_id are
// ticket-specific.
export const TICKET_CONDITION_FIELDS = [
  { value: 'status', label: 'Ticket Status' },
  { value: 'priority', label: 'Ticket Priority' },
  { value: 'category_id', label: 'Category' },
  { value: 'assigned_agent_id', label: 'Assigned Agent' },
  { value: 'conversation_id', label: 'Conversation ID' },
  // 2026-08-09: set by Digital Companion's crisis-keyword detector during
  // counselling — "true"/"false" as a string (not a real bool), since a
  // condition's expected_value is always a string; compare with
  // operator "equal_to" and value "true".
  { value: 'is_safety_escalation', label: 'Safety Escalation' },
];

export const ACTION_TYPES = [
  { value: 'assign_agent', label: 'Assign to Agent' },
  { value: 'assign_team', label: 'Assign to Team' },
  { value: 'remove_assignee', label: 'Remove Assignee' },
  { value: 'add_label', label: 'Add Label' },
  { value: 'remove_label', label: 'Remove Label' },
  { value: 'change_status', label: 'Change Status' },
  { value: 'change_priority', label: 'Change Priority' },
  { value: 'resolve_conversation', label: 'Resolve Conversation' },
  { value: 'send_message', label: 'Send Message' },
  { value: 'notify_agent', label: 'Notify Assigned Agent' },
  { value: 'notify_manager', label: 'Notify Manager' },
  { value: 'escalate_ticket', label: 'Escalate Ticket' },
  { value: 'create_follow_up_ticket', label: 'Flag Follow-up Ticket' },
  // 2026-08-10: turns AI on/off for the conversation behind a ticket/event —
  // see Docs/DIGITAL_COMPANION_AI_HANDOFF_PLAN.md §2.7.
  { value: 'set_ai_enabled', label: 'Turn AI On/Off' },
  // 2026-08-26: pauses the rest of the chain, then resumes on its own —
  // e.g. "notify Team A, wait 2 hours, notify Team B." Top-level only (a
  // rule's own action list), not inside a branch case — see
  // automation_engine.py's _run_actions_from / schemas/automation.py's
  // rejection of a nested `wait`.
  { value: 'wait', label: 'Wait' },
];

export const EVENT_LABELS: Record<string, string> = {
  conversation_created: 'Conversation Created',
  conversation_updated: 'Conversation Updated',
  message_created: 'Message Created',
  conversation_opened: 'Conversation Opened',
  ticket_created: 'Ticket Created',
  ticket_status_changed: 'Ticket Status Changed',
  ticket_assigned: 'Ticket Assigned',
  ticket_priority_changed: 'Ticket Priority Changed',
  sla_at_risk: 'Ticket SLA At Risk',
  sla_breached: 'Ticket SLA Breached',
};
